import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mail,
  Save,
  Send,
  Eye,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Code,
  ChevronDown,
  ChevronUp,
  Variable,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Copy,
  X,
  Download,
  Upload,
  History,
  RotateCcw,
  ArrowLeftRight,
  Shield,
  FileText,
  Settings,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminFetch } from '../lib/api';

interface EmailTemplate {
  id: string;
  slug: string;
  name: string;
  subject: string;
  body_html: string;
  description: string;
  variables: { key: string; label: string; example: string }[];
  is_active: boolean;
  version: number;
  last_sent_at: string | null;
  send_count: number;
  created_at: string;
  updated_at: string;
  // ETOS fields
  mail_class: string;
  purpose: string;
  trigger_event: string;
  legal_category: string;
  requires_marketing_opt_in: boolean;
  preference_flag: string;
  send_priority: string;
  from_name: string;
  from_email: string;
  reply_to: string;
  preheader: string;
  text_body: string;
  required_variables: string[];
  optional_variables: string[];
  fallback_rules: { variable: string; action: string }[];
  footer_type: string;
  language: string;
  tags: string[];
  cooldown_hours: number;
  deduplication_key: string;
  send_window_type: string;
  allow_resend: boolean;
  visible_in_user_preferences: boolean;
  internal_owner: string;
}

interface VersionEntry {
  id: string;
  version: number;
  name: string;
  subject: string;
  body_html?: string;
  description?: string;
  variables?: any[];
  created_at: string;
}

export const EmailTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<EmailTemplate | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    slug: '',
    name: '',
    subject: '',
    description: '',
  });
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState<'success' | 'error'>('success');
  const [editVars, setEditVars] = useState(false);

  // Version history
  const [versions, setVersions] = useState<VersionEntry[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Diff preview
  const [showDiff, setShowDiff] = useState(false);
  const [diffOld, setDiffOld] = useState<{
    html: string;
    subject: string;
    version: number;
    body_html: string;
  } | null>(null);
  const [diffNew, setDiffNew] = useState<{
    html: string;
    subject: string;
    version: number;
    body_html: string;
  } | null>(null);
  const [diffMode, setDiffMode] = useState<'rendered' | 'code'>('rendered');
  const [restoreVersion, setRestoreVersion] = useState<number | null>(null);

  // Import
  const [showImportPreview, setShowImportPreview] = useState(false);
  const [importData, setImportData] = useState<any>(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ETOS: Compliance + Metadata
  const [complianceResult, setComplianceResult] = useState<any>(null);
  const [showEtosPanel, setShowEtosPanel] = useState(false);
  const [showTextBody, setShowTextBody] = useState(false);

  const showStatus = (msg: string, type: 'success' | 'error' = 'success') => {
    setStatus(msg);
    setStatusType(type);
    setTimeout(() => setStatus(''), 4000);
  };

  const loadTemplates = useCallback(async () => {
    try {
      const resp = await adminFetch(`/api/email/admin/templates`, {});
      const data = await resp.json();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleSelect = async (tmpl: EmailTemplate) => {
    setSelected({ ...tmpl });
    setShowCode(false);
    setEditVars(false);
    setShowVersions(false);
    setVersions([]);
    setComplianceResult(null);
    setShowEtosPanel(false);
    setShowTextBody(false);
    // Auto-load preview
    try {
      const vars: Record<string, string> = {};
      for (const v of tmpl.variables) {
        vars[v.key] = v.example;
      }
      const resp = await adminFetch(`/api/email/admin/templates/${tmpl.id}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variables: vars }),
      });
      const data = await resp.json();
      setPreviewHtml(data.html);
      setPreviewSubject(data.subject);
      setShowPreview(true);
    } catch {
      setShowPreview(false);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const resp = await adminFetch(`/api/email/admin/templates/${selected.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: selected.name,
          subject: selected.subject,
          body_html: selected.body_html,
          description: selected.description,
          variables: selected.variables,
          is_active: selected.is_active,
        }),
      });
      const data = await resp.json();
      if (data.template) {
        setSelected(data.template);
        showStatus('Template gespeichert');
        loadTemplates();
        if (showVersions) loadVersions(data.template.id);
      }
    } catch (err: any) {
      showStatus(`Fehler: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!selected) return;
    try {
      const vars: Record<string, string> = {};
      for (const v of selected.variables) {
        vars[v.key] = v.example;
      }
      const resp = await adminFetch(`/api/email/admin/templates/${selected.id}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variables: vars }),
      });
      const data = await resp.json();
      setPreviewHtml(data.html);
      setPreviewSubject(data.subject);
      setShowPreview(true);
      setShowCode(false);
    } catch (err) {
      console.error('Preview error:', err);
    }
  };

  const handleTestSend = async () => {
    if (!selected || !testEmail) return;
    setTesting(true);
    try {
      const resp = await adminFetch(`/api/email/admin/templates/${selected.id}/test-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: testEmail }),
      });
      const data = await resp.json();
      if (data.success) {
        showStatus(`Test-Mail an ${testEmail} gesendet`);
        setShowTestModal(false);
      } else {
        showStatus(data.message || 'Fehler beim Senden', 'error');
      }
    } catch (err: any) {
      showStatus(`Fehler: ${err.message}`, 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleCreate = async () => {
    if (!newTemplate.slug || !newTemplate.name || !newTemplate.subject) return;
    try {
      const resp = await adminFetch(`/api/email/admin/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newTemplate,
          body_html:
            '<h2 style="margin:0 0 16px;color:#1a1a1a;font-size:22px;">Titel</h2>\n<p style="color:#555;font-size:15px;line-height:1.6;">Inhalt hier...</p>\n{{cta:Jetzt starten →:https://app.fahryo.de}}',
          variables: [],
        }),
      });
      const data = await resp.json();
      if (data.template) {
        showStatus('Template erstellt');
        setShowNewModal(false);
        setNewTemplate({ slug: '', name: '', subject: '', description: '' });
        loadTemplates();
        setSelected(data.template);
      }
    } catch (err: any) {
      showStatus(`Fehler: ${err.message}`, 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Template wirklich löschen?')) return;
    try {
      await adminFetch(`/api/email/admin/templates/${id}`, {
        method: 'DELETE',
      });
      showStatus('Template gelöscht');
      if (selected?.id === id) setSelected(null);
      loadTemplates();
    } catch (err: any) {
      showStatus(`Fehler: ${err.message}`, 'error');
    }
  };

  const addVariable = () => {
    if (!selected) return;
    setSelected({
      ...selected,
      variables: [...selected.variables, { key: '', label: '', example: '' }],
    });
  };

  const updateVariable = (index: number, field: string, value: string) => {
    if (!selected) return;
    const vars = [...selected.variables];
    (vars[index] as any)[field] = value;
    setSelected({ ...selected, variables: vars });
  };

  const removeVariable = (index: number) => {
    if (!selected) return;
    const vars = selected.variables.filter((_, i) => i !== index);
    setSelected({ ...selected, variables: vars });
  };

  // ─── Compliance Check ─────────────────────────────────────────

  const handleComplianceCheck = async () => {
    if (!selected) return;
    try {
      const resp = await adminFetch(`/api/email/admin/templates/${selected.id}/compliance-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await resp.json();
      setComplianceResult(data);
      if (data.compliant) {
        showStatus('Compliance-Check bestanden');
      } else {
        showStatus(`${data.errors?.length || 0} Compliance-Fehler gefunden`, 'error');
      }
    } catch (err: any) {
      showStatus(`Fehler: ${err.message}`, 'error');
    }
  };

  // ─── Version History ─────────────────────────────────────────

  const loadVersions = async (templateId: string) => {
    setLoadingVersions(true);
    try {
      const resp = await adminFetch(`/api/email/admin/templates/${templateId}/versions`, {});
      const data = await resp.json();
      setVersions(data.versions || []);
    } catch (err) {
      console.error('Failed to load versions:', err);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleShowDiff = async (version: VersionEntry) => {
    if (!selected) return;
    try {
      // Fetch full version content
      const vResp = await adminFetch(
        `/api/email/admin/templates/${selected.id}/versions/${version.version}`,
        {}
      );
      const vData = await vResp.json();
      const oldVersion = vData.version;

      // Render old version
      const oldResp = await adminFetch(`/api/email/admin/templates/preview-raw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body_html: oldVersion.body_html,
          subject: oldVersion.subject,
          variables: oldVersion.variables,
        }),
      });
      const oldRendered = await oldResp.json();

      // Render current version
      const newResp = await adminFetch(`/api/email/admin/templates/preview-raw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body_html: selected.body_html,
          subject: selected.subject,
          variables: selected.variables,
        }),
      });
      const newRendered = await newResp.json();

      setDiffOld({
        html: oldRendered.html,
        subject: oldRendered.subject,
        version: oldVersion.version,
        body_html: oldVersion.body_html,
      });
      setDiffNew({
        html: newRendered.html,
        subject: newRendered.subject,
        version: selected.version,
        body_html: selected.body_html,
      });
      setRestoreVersion(version.version);
      setShowDiff(true);
    } catch (err) {
      console.error('Diff error:', err);
    }
  };

  const handleRestore = async () => {
    if (!selected || restoreVersion === null) return;
    try {
      const resp = await adminFetch(
        `/api/email/admin/templates/${selected.id}/restore/${restoreVersion}`,
        {
          method: 'POST',
        }
      );
      const data = await resp.json();
      if (data.template) {
        setSelected(data.template);
        showStatus(`Version ${restoreVersion} wiederhergestellt`);
        setShowDiff(false);
        loadTemplates();
        loadVersions(selected.id);
      }
    } catch (err: any) {
      showStatus(`Fehler: ${err.message}`, 'error');
    }
  };

  // ─── Export / Import ─────────────────────────────────────────

  const handleExport = async () => {
    try {
      const resp = await adminFetch(`/api/email/admin/templates-export`, {});
      const data = await resp.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fahryo-email-templates-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showStatus('Templates exportiert');
    } catch (err: any) {
      showStatus(`Fehler: ${err.message}`, 'error');
    }
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target?.result as string);
        if (!data.templates || !Array.isArray(data.templates)) {
          showStatus('Ungültige Datei: templates Array fehlt', 'error');
          return;
        }
        setImportData(data);
        setShowImportPreview(true);
      } catch {
        showStatus('Ungültige JSON-Datei', 'error');
      }
    };
    reader.readAsText(file);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImportConfirm = async () => {
    if (!importData) return;
    setImporting(true);
    try {
      const resp = await adminFetch(`/api/email/admin/templates-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templates: importData.templates }),
      });
      const data = await resp.json();
      if (data.success) {
        const actions = data.results.map((r: any) => `${r.slug}: ${r.action}`).join(', ');
        showStatus(`Import erfolgreich: ${actions}`);
        setShowImportPreview(false);
        setImportData(null);
        loadTemplates();
      } else {
        showStatus('Import fehlgeschlagen', 'error');
      }
    } catch (err: any) {
      showStatus(`Fehler: ${err.message}`, 'error');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>
            <Mail size={24} /> E-Mail Templates
          </h1>
        </div>
        <div className="flex gap-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="skeleton"
              style={{ width: '100%', height: 80, borderRadius: 12 }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <Mail size={24} /> E-Mail Templates
        </h1>
        <div className="flex gap-2">
          <button
            className="btn btn-secondary"
            onClick={handleExport}
            title="Alle Templates als JSON exportieren"
          >
            <Download size={14} /> Export
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            title="Templates aus JSON importieren"
          >
            <Upload size={14} /> Import
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImportFile}
            style={{ display: 'none' }}
          />
          <button className="btn btn-primary" onClick={() => setShowNewModal(true)}>
            <Plus size={14} /> Neues Template
          </button>
        </div>
      </div>

      {status && (
        <div
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13,
            fontWeight: 500,
            background: statusType === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
            color: statusType === 'success' ? '#16a34a' : '#dc2626',
            border: `1px solid ${statusType === 'success' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
          }}
        >
          {statusType === 'success' ? (
            <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          ) : (
            <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          )}
          {status}
        </div>
      )}

      <div
        style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}
      >
        {/* Template List — grouped by language */}
        <div
          className="card"
          style={{
            padding: 0,
            overflow: 'hidden',
            maxHeight: 'calc(100vh - 160px)',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border)',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            📧 Templates ({templates.length})
          </div>
          {(() => {
            const LANG_LABELS: Record<string, string> = {
              de: '🇩🇪 Deutsch',
              en: '🇬🇧 English',
              tr: '🇹🇷 Türkçe',
              ar: '🇸🇦 العربية',
              prs: '🇦🇫 دری',
              ur: '🇵🇰 اردو',
              uk: '🇺🇦 Українська',
              hi: '🇮🇳 हिन्दी',
              fa: '🇮🇷 فارسی',
            };
            const LANG_ORDER = ['de', 'en', 'tr', 'uk', 'ar', 'ur', 'prs', 'fa', 'hi'];
            const grouped = new Map<string, EmailTemplate[]>();
            for (const tmpl of templates) {
              const lang = tmpl.language || 'de';
              if (!grouped.has(lang)) grouped.set(lang, []);
              grouped.get(lang)!.push(tmpl);
            }
            const sortedLangs = [...grouped.keys()].sort((a, b) => {
              const ai = LANG_ORDER.indexOf(a);
              const bi = LANG_ORDER.indexOf(b);
              return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
            });
            return sortedLangs.map((lang) => (
              <div key={lang}>
                <div
                  style={{
                    padding: '8px 20px',
                    background: 'var(--bg-secondary)',
                    borderBottom: '1px solid var(--border)',
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  {LANG_LABELS[lang] || lang} ({grouped.get(lang)!.length})
                </div>
                {grouped.get(lang)!.map((tmpl) => (
                  <div
                    key={tmpl.id}
                    onClick={() => handleSelect(tmpl)}
                    style={{
                      padding: '12px 20px',
                      cursor: 'pointer',
                      background: selected?.id === tmpl.id ? 'rgba(224,55,5,0.06)' : undefined,
                      borderLeft:
                        selected?.id === tmpl.id
                          ? '3px solid var(--primary)'
                          : '3px solid transparent',
                      borderBottom: '1px solid var(--border)',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{tmpl.name}</div>
                        <div className="text-xs text-muted" style={{ marginTop: 2 }}>
                          {tmpl.slug}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!tmpl.is_active && (
                          <span className="text-xs" style={{ color: '#f59e0b', fontWeight: 600 }}>
                            Inaktiv
                          </span>
                        )}
                        <span className="text-xs text-muted">v{tmpl.version}</span>
                      </div>
                    </div>
                    {tmpl.send_count > 0 && (
                      <div className="text-xs text-muted" style={{ marginTop: 4 }}>
                        📤 {tmpl.send_count}× gesendet
                        {tmpl.last_sent_at &&
                          ` · ${new Date(tmpl.last_sent_at).toLocaleDateString('de-DE')}`}
                      </div>
                    )}
                    {tmpl.mail_class && (
                      <div style={{ marginTop: 4 }}>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '2px 6px',
                            borderRadius: 4,
                            background:
                              tmpl.mail_class === 'security'
                                ? 'rgba(239,68,68,0.1)'
                                : tmpl.mail_class === 'transactional'
                                  ? 'rgba(59,130,246,0.1)'
                                  : tmpl.mail_class === 'subscription'
                                    ? 'rgba(168,85,247,0.1)'
                                    : tmpl.mail_class === 'marketing'
                                      ? 'rgba(245,158,11,0.1)'
                                      : tmpl.mail_class === 'support'
                                        ? 'rgba(20,184,166,0.1)'
                                        : 'rgba(107,114,128,0.1)',
                            color:
                              tmpl.mail_class === 'security'
                                ? '#dc2626'
                                : tmpl.mail_class === 'transactional'
                                  ? '#2563eb'
                                  : tmpl.mail_class === 'subscription'
                                    ? '#7c3aed'
                                    : tmpl.mail_class === 'marketing'
                                      ? '#d97706'
                                      : tmpl.mail_class === 'support'
                                        ? '#0d9488'
                                        : '#6b7280',
                            textTransform: 'uppercase' as const,
                            letterSpacing: '0.5px',
                          }}
                        >
                          {tmpl.mail_class}
                        </span>
                        {tmpl.send_priority === 'critical' && (
                          <span style={{ fontSize: 10, marginLeft: 4 }}>🔴</span>
                        )}
                        {tmpl.send_priority === 'high' && (
                          <span style={{ fontSize: 10, marginLeft: 4 }}>🟡</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>

        {/* Editor Panel */}
        {selected ? (
          <div>
            {/* Header */}
            <div className="card mb-3" style={{ padding: 16 }}>
              <div className="flex justify-between items-center">
                <div>
                  <input
                    className="input"
                    value={selected.name}
                    onChange={(e) => setSelected({ ...selected, name: e.target.value })}
                    style={{
                      fontWeight: 700,
                      fontSize: 18,
                      border: 'none',
                      padding: 0,
                      background: 'none',
                      width: '100%',
                    }}
                  />
                  <div
                    className="text-xs text-muted"
                    style={{
                      marginTop: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>
                      Slug: <code>{selected.slug}</code> · Version {selected.version} ·
                    </span>
                    {selected.is_active ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          color: '#16a34a',
                        }}
                      >
                        <CheckCircle size={12} /> Aktiv
                      </span>
                    ) : (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          color: '#999',
                        }}
                      >
                        <ToggleLeft size={12} /> Inaktiv
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setShowVersions(!showVersions);
                      if (!showVersions) loadVersions(selected.id);
                    }}
                    title="Versionshistorie"
                  >
                    <History size={14} /> Versionen
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setSelected({ ...selected, is_active: !selected.is_active })}
                    title={selected.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    {selected.is_active ? (
                      <ToggleRight size={18} style={{ color: '#16a34a' }} />
                    ) : (
                      <ToggleLeft size={18} style={{ color: '#999' }} />
                    )}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleDelete(selected.id)}
                    style={{ color: 'var(--danger)' }}
                    title="Löschen"
                  >
                    <Trash2 size={14} />
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={handleComplianceCheck}
                    title="Compliance-Check"
                  >
                    <Shield size={14} /> Check
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowEtosPanel(!showEtosPanel)}
                    title="ETOS-Metadaten"
                  >
                    <Settings size={14} /> ETOS
                  </button>
                </div>
              </div>
            </div>

            {/* Version History Panel */}
            {showVersions && (
              <div className="card mb-3" style={{ padding: 16 }}>
                <div className="flex justify-between items-center" style={{ marginBottom: 12 }}>
                  <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                    <History size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Versionshistorie
                  </h4>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowVersions(false)}>
                    <X size={14} />
                  </button>
                </div>
                {loadingVersions ? (
                  <div
                    className="skeleton"
                    style={{ width: '100%', height: 40, borderRadius: 8 }}
                  />
                ) : versions.length === 0 ? (
                  <div className="text-sm text-muted">Keine früheren Versionen vorhanden</div>
                ) : (
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {versions.map((v) => (
                      <div
                        key={v.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderRadius: 8,
                          marginBottom: 4,
                          fontSize: 13,
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid var(--border)',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.background =
                            'rgba(59,130,246,0.08)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.background =
                            'rgba(255,255,255,0.03)';
                        }}
                        onClick={() => handleShowDiff(v)}
                      >
                        <div>
                          <span style={{ fontWeight: 600 }}>v{v.version}</span>
                          <span className="text-muted" style={{ marginLeft: 8 }}>
                            {v.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted">
                            {new Date(v.created_at).toLocaleString('de-DE')}
                          </span>
                          <ArrowLeftRight size={12} style={{ color: '#3B82F6' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div className="card mb-3" style={{ padding: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>
                Beschreibung
              </label>
              <input
                className="input"
                value={selected.description}
                onChange={(e) => setSelected({ ...selected, description: e.target.value })}
                placeholder="Wann wird diese E-Mail gesendet?"
                style={{ width: '100%' }}
              />
            </div>

            {/* ETOS Compliance Results */}
            {complianceResult && (
              <div
                className="card mb-3"
                style={{
                  padding: 16,
                  borderLeft: complianceResult.compliant
                    ? '3px solid #16a34a'
                    : '3px solid #dc2626',
                }}
              >
                <div className="flex justify-between items-center" style={{ marginBottom: 8 }}>
                  <h4
                    style={{
                      margin: 0,
                      fontSize: 14,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Shield
                      size={14}
                      style={{ color: complianceResult.compliant ? '#16a34a' : '#dc2626' }}
                    />
                    Compliance-Check
                    {complianceResult.compliant ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          color: '#16a34a',
                        }}
                      >
                        <CheckCircle size={14} /> Bestanden
                      </span>
                    ) : (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          color: '#dc2626',
                        }}
                      >
                        <AlertCircle size={14} /> Fehler
                      </span>
                    )}
                  </h4>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setComplianceResult(null)}
                  >
                    <X size={14} />
                  </button>
                </div>
                {complianceResult.errors?.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    {complianceResult.errors.map((e: any, i: number) => (
                      <div
                        key={i}
                        style={{
                          padding: '6px 10px',
                          fontSize: 12,
                          color: '#dc2626',
                          background: 'rgba(239,68,68,0.06)',
                          borderRadius: 6,
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <AlertCircle size={12} style={{ flexShrink: 0 }} />
                        <span>
                          <strong>{e.message}</strong>{' '}
                          <span className="text-muted">({e.context})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {complianceResult.warnings?.length > 0 && (
                  <div>
                    {complianceResult.warnings.map((w: any, i: number) => (
                      <div
                        key={i}
                        style={{
                          padding: '6px 10px',
                          fontSize: 12,
                          color: '#d97706',
                          background: 'rgba(245,158,11,0.06)',
                          borderRadius: 6,
                          marginBottom: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <AlertTriangle size={12} style={{ flexShrink: 0 }} />
                        <span>
                          <strong>{w.message}</strong>{' '}
                          <span className="text-muted">({w.context})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ETOS Metadata Panel */}
            {showEtosPanel && (
              <div className="card mb-3" style={{ padding: 16 }}>
                <div className="flex justify-between items-center" style={{ marginBottom: 12 }}>
                  <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                    <Settings size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    ETOS-Metadaten
                  </h4>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowEtosPanel(false)}>
                    <X size={14} />
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Mail-Klasse
                    </label>
                    <select
                      className="input"
                      value={selected.mail_class || 'service'}
                      onChange={(e) => setSelected({ ...selected, mail_class: e.target.value })}
                      style={{ width: '100%', fontSize: 12 }}
                    >
                      <option value="security">Security</option>
                      <option value="transactional">Transactional</option>
                      <option value="service">Service</option>
                      <option value="subscription">Subscription</option>
                      <option value="marketing">Marketing</option>
                      <option value="support">Support</option>
                    </select>
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Footer-Typ
                    </label>
                    <select
                      className="input"
                      value={selected.footer_type || 'service_footer'}
                      onChange={(e) => setSelected({ ...selected, footer_type: e.target.value })}
                      style={{ width: '100%', fontSize: 12 }}
                    >
                      <option value="security_footer">Security</option>
                      <option value="transactional_footer">Transactional</option>
                      <option value="service_footer">Service</option>
                      <option value="subscription_footer">Subscription</option>
                      <option value="marketing_footer">Marketing</option>
                    </select>
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Send-Priorität
                    </label>
                    <select
                      className="input"
                      value={selected.send_priority || 'normal'}
                      onChange={(e) => setSelected({ ...selected, send_priority: e.target.value })}
                      style={{ width: '100%', fontSize: 12 }}
                    >
                      <option value="critical">Critical 🔴</option>
                      <option value="high">High 🟡</option>
                      <option value="normal">Normal</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Trigger-Event
                    </label>
                    <input
                      className="input"
                      value={selected.trigger_event || ''}
                      onChange={(e) => setSelected({ ...selected, trigger_event: e.target.value })}
                      placeholder="z.B. user_registered"
                      style={{ width: '100%', fontSize: 12, fontFamily: 'monospace' }}
                    />
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      From-Email
                    </label>
                    <input
                      className="input"
                      value={selected.from_email || 'support@fahryo.de'}
                      onChange={(e) => setSelected({ ...selected, from_email: e.target.value })}
                      style={{ width: '100%', fontSize: 12 }}
                    />
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Cooldown (h)
                    </label>
                    <input
                      className="input"
                      type="number"
                      value={selected.cooldown_hours || 0}
                      onChange={(e) =>
                        setSelected({ ...selected, cooldown_hours: parseInt(e.target.value) || 0 })
                      }
                      style={{ width: '100%', fontSize: 12 }}
                    />
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Preference-Flag
                    </label>
                    <input
                      className="input"
                      value={selected.preference_flag || ''}
                      onChange={(e) =>
                        setSelected({ ...selected, preference_flag: e.target.value })
                      }
                      placeholder="z.B. email_service_enabled"
                      style={{ width: '100%', fontSize: 12, fontFamily: 'monospace' }}
                    />
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Reply-To
                    </label>
                    <input
                      className="input"
                      value={selected.reply_to || 'support@fahryo.de'}
                      onChange={(e) => setSelected({ ...selected, reply_to: e.target.value })}
                      style={{ width: '100%', fontSize: 12 }}
                    />
                  </div>
                  <div>
                    <label
                      className="text-xs"
                      style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                    >
                      Tags
                    </label>
                    <input
                      className="input"
                      value={Array.isArray(selected.tags) ? selected.tags.join(', ') : ''}
                      onChange={(e) =>
                        setSelected({
                          ...selected,
                          tags: e.target.value
                            .split(',')
                            .map((t) => t.trim())
                            .filter(Boolean),
                        })
                      }
                      placeholder="tag1, tag2"
                      style={{ width: '100%', fontSize: 12 }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label
                    className="text-xs"
                    style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}
                  >
                    Zweck / Purpose
                  </label>
                  <input
                    className="input"
                    value={selected.purpose || ''}
                    onChange={(e) => setSelected({ ...selected, purpose: e.target.value })}
                    placeholder="Wofür wird dieses Template verwendet?"
                    style={{ width: '100%', fontSize: 12 }}
                  />
                </div>
              </div>
            )}

            {/* Preheader */}
            <div className="card mb-3" style={{ padding: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>
                Preheader
              </label>
              <input
                className="input"
                value={selected.preheader || ''}
                onChange={(e) => setSelected({ ...selected, preheader: e.target.value })}
                placeholder="Vorschautext in der Inbox (optional)"
                style={{ width: '100%', fontSize: 12 }}
              />
              <div className="text-xs text-muted" style={{ marginTop: 4 }}>
                Wird in E-Mail-Clients als Vorschautext neben dem Betreff angezeigt.
              </div>
            </div>
            {/* Subject */}
            <div className="card mb-3" style={{ padding: 16 }}>
              <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>
                Betreff
              </label>
              <input
                className="input"
                value={selected.subject}
                onChange={(e) => setSelected({ ...selected, subject: e.target.value })}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }}
              />
              <div className="text-xs text-muted" style={{ marginTop: 4 }}>
                Variablen:{' '}
                {selected.variables.map((v) => (
                  <code
                    key={v.key}
                    style={{
                      margin: '0 4px',
                      padding: '1px 4px',
                      background: 'rgba(224,55,5,0.08)',
                      borderRadius: 4,
                    }}
                  >{`{{${v.key}}}`}</code>
                ))}
              </div>
            </div>

            {/* Body Editor */}
            <div className="card mb-3" style={{ padding: 16 }}>
              <div className="flex justify-between items-center" style={{ marginBottom: 8 }}>
                <label style={{ fontWeight: 600, fontSize: 13 }}>Inhalt (HTML-Body)</label>
                <div className="flex gap-1">
                  <button
                    className={`btn btn-ghost btn-sm ${showCode ? 'active' : ''}`}
                    onClick={() => {
                      setShowCode(!showCode);
                      setShowPreview(false);
                    }}
                    title="Code anzeigen"
                  >
                    <Code size={14} /> Code
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={handlePreview} title="Vorschau">
                    <Eye size={14} /> Vorschau
                  </button>
                </div>
              </div>

              {showPreview ? (
                <div>
                  <div
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px 8px 0 0',
                      background: 'var(--surface)',
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#555',
                    }}
                  >
                    📬 Betreff: {previewSubject}
                  </div>
                  <div
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: '0 0 8px 8px',
                      maxHeight: 600,
                      overflow: 'auto',
                    }}
                  >
                    <iframe
                      srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:-apple-system,sans-serif;}</style></head><body>${previewHtml}</body></html>`}
                      style={{ width: '100%', height: 500, border: 'none' }}
                      title="Email Preview"
                    />
                  </div>
                </div>
              ) : (
                <textarea
                  className="input"
                  value={selected.body_html}
                  onChange={(e) => setSelected({ ...selected, body_html: e.target.value })}
                  rows={16}
                  style={{
                    resize: 'vertical',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    lineHeight: 1.6,
                    width: '100%',
                    whiteSpace: 'pre',
                  }}
                />
              )}

              <div
                style={{
                  marginTop: 10,
                  padding: 12,
                  borderRadius: 8,
                  background: 'rgba(59,130,246,0.06)',
                  border: '1px solid rgba(59,130,246,0.15)',
                  fontSize: 12,
                }}
              >
                <strong>Syntax-Hilfe:</strong>
                <div
                  style={{
                    marginTop: 6,
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '4px 16px',
                  }}
                >
                  <div>
                    <code>{`{{variableName}}`}</code> → Variable einfügen
                  </div>
                  <div>
                    <code>{`{{cta:Button Text:https://url}}`}</code> → CTA-Button
                  </div>
                  <div>
                    <code>{`<strong>fett</strong>`}</code> → Fetter Text
                  </div>
                  <div>
                    <code>{`<br>`}</code> → Zeilenumbruch
                  </div>
                </div>
              </div>
            </div>

            {/* Text Body (Plain Text) */}
            <div className="card mb-3" style={{ padding: 16 }}>
              <div className="flex justify-between items-center" style={{ marginBottom: 8 }}>
                <label style={{ fontWeight: 600, fontSize: 13 }}>
                  <FileText size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Text-Version (Plain Text)
                </label>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowTextBody(!showTextBody)}
                >
                  {showTextBody ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {showTextBody ? 'Zuklappen' : 'Aufklappen'}
                </button>
              </div>
              {showTextBody && (
                <>
                  <textarea
                    className="input"
                    value={selected.text_body || ''}
                    onChange={(e) => setSelected({ ...selected, text_body: e.target.value })}
                    rows={10}
                    placeholder="Plain-Text-Version der E-Mail (für E-Mail-Clients ohne HTML-Unterstützung)"
                    style={{
                      resize: 'vertical',
                      fontFamily: 'monospace',
                      fontSize: 12,
                      lineHeight: 1.6,
                      width: '100%',
                      whiteSpace: 'pre',
                    }}
                  />
                  <div className="text-xs text-muted" style={{ marginTop: 4 }}>
                    Wird in E-Mail-Clients angezeigt, die kein HTML unterstützen. Variablen wie{' '}
                    <code>{`{{user.first_name}}`}</code> werden automatisch ersetzt.
                  </div>
                </>
              )}
            </div>
            {/* Variables */}
            <div className="card mb-3" style={{ padding: 16 }}>
              <div className="flex justify-between items-center" style={{ marginBottom: 8 }}>
                <label style={{ fontWeight: 600, fontSize: 13 }}>
                  <Variable size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  Variablen ({selected.variables.length})
                </label>
                <div className="flex gap-1">
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditVars(!editVars)}>
                    {editVars ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {editVars ? 'Schließen' : 'Bearbeiten'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={addVariable}>
                    <Plus size={12} />
                  </button>
                </div>
              </div>

              {!editVars ? (
                <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                  {selected.variables.map((v) => (
                    <div
                      key={v.key}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 500,
                        background: 'rgba(224,55,5,0.08)',
                        color: '#c0392b',
                      }}
                    >
                      {`{{${v.key}}}`}{' '}
                      <span style={{ color: '#999', marginLeft: 4 }}>{v.label}</span>
                    </div>
                  ))}
                  {selected.variables.length === 0 && (
                    <span className="text-xs text-muted">Keine Variablen definiert</span>
                  )}
                </div>
              ) : (
                <div>
                  {selected.variables.map((v, i) => (
                    <div key={i} className="flex gap-2 items-center" style={{ marginBottom: 6 }}>
                      <input
                        className="input"
                        value={v.key}
                        onChange={(e) => updateVariable(i, 'key', e.target.value)}
                        placeholder="key"
                        style={{ flex: 1, fontSize: 12, fontFamily: 'monospace' }}
                      />
                      <input
                        className="input"
                        value={v.label}
                        onChange={(e) => updateVariable(i, 'label', e.target.value)}
                        placeholder="Label"
                        style={{ flex: 1.5, fontSize: 12 }}
                      />
                      <input
                        className="input"
                        value={v.example}
                        onChange={(e) => updateVariable(i, 'example', e.target.value)}
                        placeholder="Beispiel"
                        style={{ flex: 1.5, fontSize: 12 }}
                      />
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => removeVariable(i)}
                        style={{ color: 'var(--danger)', padding: 4 }}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Save size={14} />
                )}
                Speichern
              </button>
              <button className="btn btn-secondary" onClick={() => setShowTestModal(true)}>
                <Send size={14} /> Test-Mail senden
              </button>
              <button className="btn btn-secondary" onClick={handlePreview}>
                <Eye size={14} /> Vorschau
              </button>
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: 40, textAlign: 'center' }}>
            <Mail size={48} style={{ color: '#ddd', marginBottom: 16 }} />
            <h3 style={{ color: '#999', fontWeight: 500 }}>Wähle ein Template zum Bearbeiten</h3>
            <p className="text-sm text-muted">
              Oder erstelle ein neues Template mit dem Button oben.
            </p>
          </div>
        )}
      </div>

      {/* ─── Diff Preview Modal ──────────────────────────────────── */}
      {showDiff && diffOld && diffNew && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            className="card"
            style={{
              padding: 0,
              width: '95vw',
              maxWidth: 1200,
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 16 }}>
                  <ArrowLeftRight size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  Vergleich: v{diffOld.version} → v{diffNew.version} (aktuell)
                </h3>
              </div>
              <div className="flex gap-2 items-center">
                <div
                  style={{
                    display: 'flex',
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid var(--border)',
                  }}
                >
                  <button
                    onClick={() => setDiffMode('rendered')}
                    style={{
                      padding: '4px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                      background: diffMode === 'rendered' ? 'var(--primary)' : 'transparent',
                      color: diffMode === 'rendered' ? '#fff' : 'var(--text-muted)',
                    }}
                  >
                    <Eye size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Vorschau
                  </button>
                  <button
                    onClick={() => setDiffMode('code')}
                    style={{
                      padding: '4px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                      background: diffMode === 'code' ? 'var(--primary)' : 'transparent',
                      color: diffMode === 'code' ? '#fff' : 'var(--text-muted)',
                    }}
                  >
                    <Code size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Code
                  </button>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowDiff(false)}>
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Side-by-side content */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                flex: 1,
                overflow: 'hidden',
              }}
            >
              {/* Old Version */}
              <div
                style={{
                  borderRight: '1px solid var(--border)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '8px 16px',
                    fontSize: 12,
                    fontWeight: 700,
                    background: 'rgba(239,68,68,0.08)',
                    color: '#EF4444',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  ← Alte Version (v{diffOld.version})
                  <span className="text-muted" style={{ marginLeft: 8, fontWeight: 400 }}>
                    Betreff: {diffOld.subject}
                  </span>
                </div>
                <div style={{ flex: 1, overflow: 'auto' }}>
                  {diffMode === 'rendered' ? (
                    <iframe
                      srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:-apple-system,sans-serif;}</style></head><body>${diffOld.html}</body></html>`}
                      style={{ width: '100%', height: '100%', minHeight: 500, border: 'none' }}
                      title="Old Version"
                    />
                  ) : (
                    <pre
                      style={{
                        margin: 0,
                        padding: 16,
                        fontSize: 11,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {diffOld.body_html}
                    </pre>
                  )}
                </div>
              </div>

              {/* New Version */}
              <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div
                  style={{
                    padding: '8px 16px',
                    fontSize: 12,
                    fontWeight: 700,
                    background: 'rgba(16,185,129,0.08)',
                    color: '#10B981',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  Aktuelle Version (v{diffNew.version}) →
                  <span className="text-muted" style={{ marginLeft: 8, fontWeight: 400 }}>
                    Betreff: {diffNew.subject}
                  </span>
                </div>
                <div style={{ flex: 1, overflow: 'auto' }}>
                  {diffMode === 'rendered' ? (
                    <iframe
                      srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:-apple-system,sans-serif;}</style></head><body>${diffNew.html}</body></html>`}
                      style={{ width: '100%', height: '100%', minHeight: 500, border: 'none' }}
                      title="New Version"
                    />
                  ) : (
                    <pre
                      style={{
                        margin: 0,
                        padding: 16,
                        fontSize: 11,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {diffNew.body_html}
                    </pre>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '12px 24px',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button className="btn btn-ghost" onClick={() => setShowDiff(false)}>
                Schließen
              </button>
              <button className="btn btn-primary" onClick={handleRestore}>
                <RotateCcw size={14} /> Version {restoreVersion} wiederherstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Import Preview Modal ────────────────────────────────── */}
      {showImportPreview && importData && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className="card"
            style={{ padding: 24, width: 600, maxHeight: '80vh', overflow: 'auto' }}
          >
            <h3 style={{ marginBottom: 4 }}>
              <Upload size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              Import Vorschau
            </h3>
            <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
              {importData.templates.length} Templates werden importiert. Bestehende Templates werden
              aktualisiert (alte Version wird gesichert).
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {importData.templates.map((tmpl: any, i: number) => {
                const existing = templates.find((t) => t.slug === tmpl.slug);
                return (
                  <div
                    key={i}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 8,
                      background: existing ? 'rgba(245,158,11,0.06)' : 'rgba(16,185,129,0.06)',
                      border: `1px solid ${existing ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)'}`,
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{tmpl.name}</span>
                        <span className="text-xs text-muted" style={{ marginLeft: 8 }}>
                          {tmpl.slug}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 4,
                          background: existing ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)',
                          color: existing ? '#D97706' : '#059669',
                        }}
                      >
                        {existing
                          ? `Update (v${existing.version} → v${(existing.version || 0) + 1})`
                          : 'Neu'}
                      </span>
                    </div>
                    <div className="text-xs text-muted" style={{ marginTop: 4 }}>
                      Betreff: {tmpl.subject}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-2 justify-end">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setShowImportPreview(false);
                  setImportData(null);
                }}
              >
                Abbrechen
              </button>
              <button
                className="btn btn-primary"
                onClick={handleImportConfirm}
                disabled={importing}
              >
                {importing ? (
                  <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Upload size={14} />
                )}
                Importieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Send Modal */}
      {showTestModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div className="card" style={{ padding: 24, width: 420 }}>
            <h3 style={{ marginBottom: 16 }}>📧 Test-Mail senden</h3>
            <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
              Sendet das Template <strong>{selected?.name}</strong> mit Beispiel-Werten an die
              angegebene Adresse.
            </p>
            <input
              className="input"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              style={{ width: '100%', marginBottom: 16 }}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button className="btn btn-ghost" onClick={() => setShowTestModal(false)}>
                Abbrechen
              </button>
              <button
                className="btn btn-primary"
                onClick={handleTestSend}
                disabled={testing || !testEmail}
              >
                {testing ? (
                  <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Send size={14} />
                )}
                Senden
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Template Modal */}
      {showNewModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div className="card" style={{ padding: 24, width: 460 }}>
            <h3 style={{ marginBottom: 16 }}>➕ Neues E-Mail Template</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label
                  className="text-xs"
                  style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}
                >
                  Slug (eindeutiger Identifier)
                </label>
                <input
                  className="input"
                  value={newTemplate.slug}
                  onChange={(e) =>
                    setNewTemplate({
                      ...newTemplate,
                      slug: e.target.value.replace(/[^a-z0-9_]/g, ''),
                    })
                  }
                  placeholder="z.B. trial_expiring"
                  style={{ width: '100%', fontFamily: 'monospace' }}
                />
              </div>
              <div>
                <label
                  className="text-xs"
                  style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}
                >
                  Name
                </label>
                <input
                  className="input"
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="z.B. Trial läuft ab"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label
                  className="text-xs"
                  style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}
                >
                  Betreff
                </label>
                <input
                  className="input"
                  value={newTemplate.subject}
                  onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
                  placeholder="z.B. Dein Trial läuft in 3 Tagen ab!"
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label
                  className="text-xs"
                  style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}
                >
                  Beschreibung (optional)
                </label>
                <input
                  className="input"
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                  placeholder="Wann wird diese E-Mail gesendet?"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end" style={{ marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={() => setShowNewModal(false)}>
                Abbrechen
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreate}
                disabled={!newTemplate.slug || !newTemplate.name || !newTemplate.subject}
              >
                <Plus size={14} /> Erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
